import {
  computeEntityGraphLayout,
  EntityGraphEdge,
  EntityGraphNode,
  entityGraphSelector,
  EntityObject,
  getObjectId,
  getObjectType,
  useGetEntityGraphQuery
} from "@ots-frontend/ots-data";
import {useEffect, useMemo} from "react";
import {
  Background,
  Controls,
  Edge,
  MarkerType,
  Node,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow
} from "@xyflow/react";
import ELK, {ElkNode} from 'elkjs/lib/elk.bundled.js';

const elk = new ELK();

const elkOptions = {
  'elk.algorithm': 'layered',
  'elk.layered.spacing.nodeNodeBetweenLayers': '100',
  'elk.spacing.nodeNode': '80',
  'org.eclipse.elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
  'org.eclipse.elk.layered.nodePlacement.bk.edgeStraightening': 'IMPROVE_STRAIGHTNESS',
};

const getLayoutedElements = (nodes, edges, options = {}) => {
  const isHorizontal = options?.['elk.direction'] === 'RIGHT';
  const graph: ElkNode = {
    id: 'root',
    layoutOptions: options,
    children: nodes.map((node) => ({
      ...node,
      // Adjust the target and source handle positions based on the layout
      // direction.
      targetPosition: isHorizontal ? 'left' : 'top',
      sourcePosition: isHorizontal ? 'right' : 'bottom',

      // Hardcode a width and height for elk to use when layouting.
      width: 200,
      height: 100,
    })),
    edges: edges,
  };

  return elk
    .layout(graph)
    .then((layoutedGraph) => ({
      nodes: layoutedGraph.children.map((node) => ({
        ...node,
        // React Flow expects a position property on the node instead of `x`
        // and `y` fields.
        position: {x: node.x, y: node.y},
      })),

      edges: layoutedGraph.edges,
    }))
    .catch(console.error);
};

export type EntityGraphViewProps = {
  entity: EntityObject
  direction?: 'PARENTS' | 'CHILDREN' | 'BOTH';
  maxUpDepth?: number;
  maxDownDepth?: number;
  bestDownTree?: boolean;
}

export const EntityGraphView = (props: EntityGraphViewProps) => {
  return (
    <ReactFlowProvider>
      <InternalEntityGraphView {...props} />
    </ReactFlowProvider>
  )
};

const InternalEntityGraphView = (props: EntityGraphViewProps) => {
  const {
    entity,
    direction = 'BOTH',
    maxUpDepth = 4,
    maxDownDepth = 4,
    bestDownTree = true,
  } = props

  const [nodes, setNodes] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);
  const {fitView} = useReactFlow();

  const selectEntityGraph = useMemo(() => {
    return entityGraphSelector();
  }, []);

  const {entityGraph, isLoading, error} = useGetEntityGraphQuery({
    entity: {
      id: getObjectId(entity),
      type: getObjectType(entity),
    },
    opts: {
      direction,
      maxUpDepth,
      maxDownDepth,
      bestDownTree,
    }
  }, {
    selectFromResult: (result) => ({
      entityGraph: selectEntityGraph(result),
      isLoading: result.isLoading,
      error: result.error
    })
  });

  useEffect(() => {
    if (entityGraph) {
      const rf = toReactFlow(entityGraph.nodes, entityGraph.edges);
      setNodes(rf.nodes);
      setEdges(rf.edges);

      const opts = {'elk.direction': 'DOWN', ...elkOptions};
      getLayoutedElements(rf.nodes, rf.edges, opts)
        .then(value => {
          if (value) {
            setNodes(value.nodes);
            setEdges(value.edges);
            fitView();
          }
        })
    }
  }, [entityGraph, setEdges, setNodes, fitView]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      proOptions={{hideAttribution: true}}
      defaultEdgeOptions={{markerEnd: {type: MarkerType.ArrowClosed}}}
      fitView
    >
      <Background gap={16}/>
      <Controls position="bottom-right" showInteractive={false}/>
    </ReactFlow>
  )
};

const colorForType = (type: string) => {
  const palette: Record<string, string> = {
    OPERATING_AREA: '#7c3aed',
    ROUTE: '#0ea5e9',
    SITE: '#22c55e',
    WELL: '#06b6d4',
    CONTAINER_CLUSTER: '#f59e0b',
    CONTAINER: '#ef4444',
    METER: '#8b5cf6',
  };
  return palette[type] ?? '#64748b';
}

const toReactFlow = (nodes: EntityGraphNode[], edges: EntityGraphEdge[]): { nodes: Node[]; edges: Edge[] } => {
  const pos = computeEntityGraphLayout(nodes);

  const rfNodes: Node[] = nodes.map(n => ({
    id: n.id,
    data: {label: n.label, type: n.type, ...n.data},
    position: pos.get(n.id) ?? {x: 0, y: 0},
    style: {
      borderRadius: 12,
      padding: 8,
      border: `2px solid ${colorForType(n.type)}`,
      background: '#ffffff',
      boxShadow: '0 8px 20px rgba(0,0,0,0.07)'
    },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  }));


  const rfEdges: Edge[] = edges.map(e => ({
    id: e.id,
    source: e.source,
    target: e.target,
    type: 'smoothstep',
    label: e.label ?? '',
    markerEnd: {type: MarkerType.ArrowClosed},
    style: {strokeWidth: 2},
    labelBgPadding: [6, 2],
    labelBgBorderRadius: 4,
    animated: true,
  }));


  return {nodes: rfNodes, edges: rfEdges};
}
